import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ArrowRightLeft, Upload, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';

interface Redirect {
  id: number;
  source_path: string;
  target_path: string;
  status_code: number;
  is_active: boolean;
  hits: number;
  last_hit_at: string | null;
  created_at: string;
}

interface Paginated<T> { data: T[]; total: number; current_page: number; last_page: number }

export const Route = createFileRoute('/_app/redirects')({
  component: RedirectsView,
});

function RedirectsView() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['redirects', q, page],
    queryFn: () => unwrap<Paginated<Redirect>>(
      api.get(`/redirects?per_page=50&page=${page}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    ),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/redirects/${id}`),
    onSuccess: () => {
      toast.success('Redirección eliminada');
      void qc.invalidateQueries({ queryKey: ['redirects'] });
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return unwrap<{ imported: number; skipped: number }>(
        api.post('/redirects/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
      );
    },
    onSuccess: (r) => {
      toast.success(`Importadas ${r.imported}, saltadas ${r.skipped}`);
      void qc.invalidateQueries({ queryKey: ['redirects'] });
    },
    onError: () => toast.error('No se pudo importar'),
  });

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Redirecciones</h1>
          <p className="text-sm text-neutral-500">
            URLs antiguas → URLs nuevas. Aplicadas a nivel middleware antes de servir contenido.
          </p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-1.5 text-xs hover:bg-neutral-50">
            <Upload className="size-3.5" /> Importar CSV
            <input
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Nueva
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{data?.total ?? 0} redirecciones</CardTitle>
              <CardDescription>Origen → Destino, código HTTP, hits totales.</CardDescription>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar…" className="pl-9" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Origen</th>
                <th className="px-4 py-2">Destino</th>
                <th className="px-4 py-2">Código</th>
                <th className="px-4 py-2">Hits</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data?.data.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Sin redirecciones.</td></tr>
              )}
              {(data?.data ?? []).map((r) => (
                <tr key={r.id} className="border-b border-neutral-100">
                  <td className="px-4 py-2 font-mono text-xs">{r.source_path}</td>
                  <td className="px-4 py-2 font-mono text-xs"><ArrowRightLeft className="mr-1 inline size-3 text-neutral-400" /> {r.target_path}</td>
                  <td className="px-4 py-2"><Badge variant="outline">{r.status_code}</Badge></td>
                  <td className="px-4 py-2 text-neutral-500">{r.hits}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => { if (confirm('¿Eliminar redirección?')) remove.mutate(r.id); }}
                      className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <CreateRedirectDialog open={createOpen} onClose={() => setCreateOpen(false)} qc={qc} />
    </div>
  );
}

function CreateRedirectDialog({
  open, onClose, qc,
}: { open: boolean; onClose: () => void; qc: ReturnType<typeof useQueryClient> }) {
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [code, setCode] = useState(301);

  const create = useMutation({
    mutationFn: () => api.post('/redirects', { source_path: source, target_path: target, status_code: code }),
    onSuccess: () => {
      toast.success('Redirección creada');
      void qc.invalidateQueries({ queryKey: ['redirects'] });
      onClose();
      setSource('');
      setTarget('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Error al crear'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nueva redirección</DialogTitle>
          <DialogDescription>El path origen recibirá una redirección al destino.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Origen</Label>
            <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="/url-antigua" className="font-mono text-xs" />
          </div>
          <div>
            <Label>Destino</Label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="/url-nueva o https://externo.com" className="font-mono text-xs" />
          </div>
          <div>
            <Label>Código HTTP</Label>
            <select
              value={code}
              onChange={(e) => setCode(Number(e.target.value))}
              className="w-full rounded border border-neutral-200 px-3 py-2 text-sm"
            >
              <option value={301}>301 - Permanente</option>
              <option value={302}>302 - Temporal</option>
              <option value={307}>307 - Temporal (preserve method)</option>
              <option value={308}>308 - Permanente (preserve method)</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!source || !target || create.isPending} onClick={() => create.mutate()}>
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
